import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../../API";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import Topbar from "../../components/Topbar/Topbar";
import {
  FaArrowLeft,
  FaTicketAlt,
  FaUserShield,
  FaUserCircle,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaPaperPlane,
  FaLock,
} from "react-icons/fa";
import "./ticketDetails.css";

const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const groupMessagesByDay = (messages) => {
  const groups = [];
  let current = null;
  for (const m of messages) {
    const day = new Date(m.created_at).toDateString();
    if (!current || current.day !== day) {
      current = { day, label: new Date(m.created_at).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" }), items: [] };
      groups.push(current);
    }
    current.items.push(m);
  }
  return groups;
};

const StatusChip = ({ state, t }) => {
  const s = (state || "").toUpperCase();
  let tone = "muted", Icon = FaClock;
  if (s === "OPEN" || s === "PENDING" || s === "IN_PROGRESS") { tone = "warn"; Icon = FaClock; }
  else if (s === "RESOLVED" || s === "CLOSED") { tone = "success"; Icon = FaCheckCircle; }
  else if (s === "REJECTED") { tone = "danger"; Icon = FaExclamationTriangle; }
  return (
    <span className={`td-chip td-chip--${tone}`}>
      <Icon />
      <span>{state}</span>
    </span>
  );
};

const TicketDetails = () => {
  const { t } = useTranslation();
  const { ticketId } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchTicketDetails = async () => {
      const token = localStorage.getItem("protoken");
      if (!token) { navigate("/login"); return; }
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await API.get(`/support-tickets/${ticketId}`, config);
        setTicket(response.data);
      } catch (error) {
        console.error("Error fetching ticket details:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTicketDetails();
  }, [ticketId, navigate]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [ticket]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    const token = localStorage.getItem("protoken");
    if (!token) { navigate("/login"); return; }
    setSending(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await API.post(
        `/support-tickets/message/${ticketId}`,
        { message: newMessage, senderType: "USER" },
        config
      );
      setNewMessage("");
      const response = await API.get(`/support-tickets/${ticketId}`, config);
      setTicket(response.data);
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="Page_ticket_details">
        <Topbar />
        <Navbar />
        <main className="td-main">
          <div className="td-empty">
            <div className="td-spinner" />
            <p>{t("ticketDetails.loading")}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="Page_ticket_details">
        <Topbar />
        <Navbar />
        <main className="td-main">
          <div className="td-empty">
            <FaExclamationTriangle className="td-empty__icon" />
            <h2>{t("ticketDetails.ticket_not_found")}</h2>
            <button className="td-btn td-btn--primary" onClick={() => navigate("/profile?tab=4")}>
              <FaArrowLeft /> {t("ticketDetails.back_button")}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const sortedMessages = [...ticket.messages].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );
  const groups = groupMessagesByDay(sortedMessages);
  const shortId = (ticket.id || "").slice(0, 8).toUpperCase();
  const isClosed = (ticket.state || "").toUpperCase() === "CLOSED";

  return (
    <div className="Page_ticket_details">
      <Topbar />
      <Navbar />

      <section className="td-hero">
        <div className="td-hero__inner">
          <button className="td-back" onClick={() => navigate("/profile?tab=4")}>
            <FaArrowLeft /> <span>{t("ticketDetails.back_button")}</span>
          </button>

          <div className="td-hero__top">
            <div className="td-hero__badge">
              <FaTicketAlt />
            </div>
            <div className="td-hero__text">
              <span className="td-hero__eyebrow">
                {t("ticketDetails.ticket_header", { ticketId: `#${shortId}` })}
              </span>
              <h1 className="td-hero__title">{ticket.subject}</h1>
            </div>
            <StatusChip state={ticket.state} t={t} />
          </div>

          <div className="td-meta">
            <div className="td-meta__item">
              <span className="td-meta__label">
                <FaUserShield /> {t("ticketDetails.admin_label")}
              </span>
              <span className="td-meta__value">
                {ticket.adminUsername || t("ticketDetails.not_assigned")}
              </span>
            </div>
            <div className="td-meta__item">
              <span className="td-meta__label">
                <FaClock /> Created
              </span>
              <span className="td-meta__value">
                {formatDateTime(ticket.created_at || sortedMessages[0]?.created_at)}
              </span>
            </div>
            <div className="td-meta__item">
              <span className="td-meta__label">
                <FaPaperPlane /> Messages
              </span>
              <span className="td-meta__value">{sortedMessages.length}</span>
            </div>
          </div>
        </div>
      </section>

      <main className="td-main">
        <div className="td-chat">
          <div className="td-thread" ref={scrollRef}>
            {groups.length === 0 && (
              <p className="td-thread__empty">No messages yet.</p>
            )}
            {groups.map((g) => (
              <div key={g.day} className="td-day">
                <div className="td-day__sep">
                  <span>{g.label}</span>
                </div>
                {g.items.map((m) => {
                  const isUser = String(m.senderType).toUpperCase() === "USER";
                  return (
                    <div key={m.id} className={`td-msg ${isUser ? "td-msg--user" : "td-msg--admin"}`}>
                      <div className="td-msg__avatar">
                        {isUser ? <FaUserCircle /> : <FaUserShield />}
                      </div>
                      <div className="td-msg__body">
                        <div className="td-msg__bubble">{m.message}</div>
                        <span className="td-msg__time">
                          {new Date(m.created_at).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {isClosed ? (
            <div className="td-closed">
              <FaLock />
              <p>This ticket is closed. You can't send new messages.</p>
            </div>
          ) : (
            <div className="td-composer">
              <textarea
                className="td-composer__input"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t("ticketDetails.message_placeholder")}
                rows={2}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <button
                className="td-composer__send"
                onClick={handleSendMessage}
                disabled={sending || !newMessage.trim()}
              >
                <FaPaperPlane />
                <span>{t("ticketDetails.send_button")}</span>
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TicketDetails;
